import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { AIText } from "@/components/AIText";
import { downloadReport } from "@/lib/report";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Bookmark, TrendingUp, GitCompareArrows, Trash2, Download, ChevronDown, Share2, Copy, Check, Mail, ShieldOff, Clock } from "lucide-react";

const kindMeta = (k) => k === "premium_change"
  ? { icon: TrendingUp, labelKey: "kind_premium", color: "text-amber-500 bg-amber-50" }
  : { icon: GitCompareArrows, labelKey: "kind_comparison", color: "text-blue-500 bg-blue-50" };

export default function SavedAnalyses() {
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareTitle, setShareTitle] = useState("");
  const [shareItem, setShareItem] = useState(null);
  const [expiry, setExpiry] = useState("never");
  const [copied, setCopied] = useState(false);

  const load = () => api.get("/saved-analyses").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const del = async (e, id) => { e.stopPropagation(); await api.delete(`/saved-analyses/${id}`); load(); };
  const exportPdf = (e, item) => {
    e.stopPropagation();
    downloadReport({ title: item.title, language: lang, sections: [{ heading: t(kindMeta(item.kind).labelKey), body: item.content }] });
  };
  const doShare = async (item, expiresDays) => {
    try {
      const r = await api.post(`/saved-analyses/${item.id}/share`, { expires_days: expiresDays ?? null });
      setShareUrl(`${window.location.origin}/shared/${r.data.token}`);
      setShareTitle(item.title);
      setShareItem(item);
      setCopied(false);
      return r.data;
    } catch { toast.error(t("could_not_link")); }
  };
  const share = async (e, item) => {
    e.stopPropagation();
    setExpiry("never");
    await doShare(item, null);
  };
  const changeExpiry = async (val) => {
    setExpiry(val);
    const map = { never: null, "7": 7, "30": 30 };
    await doShare(shareItem, map[val]);
    toast.success(t("share_updated"));
  };
  const revoke = async () => {
    if (!shareItem) return;
    try {
      await api.delete(`/saved-analyses/${shareItem.id}/share`);
      toast.success(t("link_revoked"));
      setShareUrl(null); setShareItem(null);
      load();
    } catch { toast.error(t("could_not_link")); }
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.message("Copy the link from the field above.");
    }
  };
  const emailLink = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent("CoverGuide: " + shareTitle)}&body=${encodeURIComponent("Here's an insurance analysis from CoverGuide:\n\n" + shareUrl)}`;
  };

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><Bookmark className="h-7 w-7 text-brandblue" /> {t("saved_title")}</h1>
      <p className="mt-1 text-slate-500">{t("saved_sub")}</p>

      <div className="mt-6 space-y-3">
        {items.length === 0 && <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center text-slate-400">{t("saved_empty")}</div>}
        {items.map((item) => {
          const m = kindMeta(item.kind);
          const open = openId === item.id;
          return (
            <div key={item.id} data-testid={`saved-${item.id}`} className="rounded-2xl bg-white border border-slate-100 card-shadow overflow-hidden">
              <div role="button" tabIndex={0} onClick={() => setOpenId(open ? null : item.id)}
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-slate-50">
                <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${m.color}`}><m.icon className="h-5 w-5" /></span>
                <div className="flex-1 min-w-0"><p className="font-medium text-navy truncate">{item.title}</p><p className="text-xs text-slate-400">{t(m.labelKey)} · {new Date(item.created_at).toLocaleDateString()}</p></div>
                <button onClick={(e) => share(e, item)} data-testid={`saved-share-${item.id}`} className="rounded-full p-2 text-slate-400 hover:text-brandteal hover:bg-teal-50"><Share2 className="h-4 w-4" /></button>
                <button onClick={(e) => exportPdf(e, item)} data-testid={`saved-export-${item.id}`} className="rounded-full p-2 text-slate-400 hover:text-brandblue hover:bg-blue-50"><Download className="h-4 w-4" /></button>
                <button onClick={(e) => del(e, item.id)} data-testid={`saved-del-${item.id}`} className="rounded-full p-2 text-slate-300 hover:text-red-500 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                <ChevronDown className={`h-5 w-5 text-slate-300 transition-transform ${open ? "rotate-180" : ""}`} />
              </div>
              {open && (
                <div className="border-t border-slate-100 p-5">
                  {item.meta && item.kind === "premium_change" && item.meta.percent !== undefined && (
                    <p className="mb-3 text-sm font-medium text-navy">{t("change")}: {item.meta.difference >= 0 ? "+" : ""}${Number(item.meta.difference).toLocaleString()} ({item.meta.percent}%)</p>
                  )}
                  <AIText>{item.content}</AIText>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!shareUrl} onOpenChange={(o) => { if (!o) setShareUrl(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("share_analysis")}</DialogTitle>
            <DialogDescription>{t("share_desc", { title: shareTitle })}</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input data-testid="share-url" readOnly value={shareUrl || ""} className="rounded-xl text-sm" onFocus={(e) => e.target.select()} />
            <Button data-testid="share-copy" onClick={copyLink} className="rounded-xl brand-gradient text-white px-3">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button>
          </div>

          <div className="mt-1">
            <p className="mb-1.5 flex items-center gap-1.5 text-sm font-medium text-navy"><Clock className="h-4 w-4 text-slate-400" /> {t("link_expiry")}</p>
            <div className="flex gap-2" data-testid="share-expiry-group">
              {[["never", t("expiry_never")], ["7", t("expiry_7")], ["30", t("expiry_30")]].map(([val, label]) => (
                <button key={val} data-testid={`share-expiry-${val}`} onClick={() => changeExpiry(val)}
                  className={`flex-1 rounded-full border px-3 py-2 text-sm font-medium transition-colors ${expiry === val ? "border-brandblue bg-brandblue/10 text-brandblue" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <Button data-testid="share-email" variant="outline" onClick={emailLink} className="rounded-full flex-1"><Mail className="h-4 w-4 mr-2" />{t("email_link")}</Button>
            <Button data-testid="share-revoke" variant="outline" onClick={revoke} className="rounded-full flex-1 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"><ShieldOff className="h-4 w-4 mr-2" />{t("revoke_link")}</Button>
          </div>
          <p className="text-xs text-slate-400">{t("revoke_hint")}</p>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
